import * as Cookies from 'js-cookie';

import {
  CookieServiceOptions,
  createEnsightenConsentManager,
} from '@oneaudi/audi-cookie-service';
import { AudiColaServiceOptions } from '@oneaudi/onegraph-service-v4';
import { AudiConfiguratorLinkServiceOptions } from '@oneaudi/configurator-link-service';
import { AudiMarketContextServiceOptions } from '@volkswagen-onehub/audi-market-context-service';
import { AuthServiceOptions } from '@oneaudi/audi-auth-service';
import { CarlineLinkServiceOptions } from '@oneaudi/carline-link-service';
import { CurrentCarlineServiceConfig } from '@oneaudi/current-carline-service';
import { DisclaimerManagerOptions } from '@volkswagen-onehub/disclaimer-manager';
import { EnvConfigServiceOptions } from '@oneaudi/audi-env-config-service';
import { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import { I18NServiceOptions } from '@oneaudi/i18n-service';
import { LocaleServiceConfig } from '@volkswagen-onehub/locale-service';
import { NavigationServiceOptions } from '@volkswagen-onehub/navigation-service';
import { OneGraphServiceOptions } from '@oneaudi/onegraph-service';
import { RenderMode } from '@oneaudi/render-mode-service';
import { StateManagerServiceOptions } from '@oneaudi/confi-state-manager-service';
import { TrackingServiceOptions } from '@oneaudi/audi-tracking-service';
import { VueFormatterServiceOptions } from '@oneaudi/vue-formatter-service';
import { createFallbackConsentManager } from './fallback-consent-manager';
import { createLogger } from '../logger';
import { getSysEnv } from './sysenv';
import { removePrivateData } from './tracking-url-sanitizer';

const getDefaultLogoutRedirectUrl = (dom: Element): string => {
  const logoutUrl = getMetaContent(dom, 'myaudi-logout-url');
  if (!logoutUrl) {
    return location.href;
  }

  return logoutUrl;
};

const metaValues = {};

const getApiGatewayUrl = (dom: Element): string =>
  getMetaContent(dom, 'wag-baseurl');

const getAuthProxyUrl = (dom: Element): string => {
  const myAudiAuthProxy = getMetaContent(dom, 'myaudi-auth-proxy');
  if (myAudiAuthProxy) {
    return myAudiAuthProxy;
  }
};

const getMetaContent = (dom: Element, metaName: string): string => {
  if (!metaValues[metaName] || dom != metaValues[metaName].dom) {
    const metatag = dom.querySelector(`meta[name=${metaName}]`);
    metaValues[metaName] = {
      dom,
      value: metatag?.getAttribute('content') || '',
    };
  }
  return metaValues[metaName].value;
};

const getAuthOptions = (dom: Element): AuthServiceOptions => {
  // get proxy URL
  const authProxyUrl = getAuthProxyUrl(dom);

  // get logout URL
  const defaultLogoutRedirectUrl = getDefaultLogoutRedirectUrl(dom);

  return {
    authProxyUrl,
    defaultLoginRedirectUrl: location.href,
    defaultLogoutRedirectUrl,
    fagScopeMappings: new Map<string, string[]>([['myaudi', []]]),
    ssoUrl: location.href,
  };
};

const getDpuApiGatewayUrl = (dom: Element): string => {
  const apiGateWayUrl = getApiGatewayUrl(dom);
  const localeServiceDefinition = getLocaleOptions(dom);
  const language = localeServiceDefinition.language;
  const country = localeServiceDefinition.countryCode.toLowerCase();

  return `${apiGateWayUrl}/dpu/v1/${country}/${language}`;
};

const getMarketContextApiGatewayUrl = (dom: Element): string => {
  const apiGateWayUrl = getApiGatewayUrl(dom);
  const localeServiceDefinition = getLocaleOptions(dom);
  const language = localeServiceDefinition.language;
  const country = localeServiceDefinition.countryCode.toLowerCase();

  return `${apiGateWayUrl}/market-context/v1/${country}/${language}`;
};

const getPreviewToken = (dom: Element): string => {
  let jwtToken = '';

  const isPreview = Cookies.get('nemo_preview');

  if (isPreview === 'true') {
    const locale = getLocaleOptions(dom);
    const jwtCookieName = `jwt_${locale.countryCode}_${locale.language}`;
    jwtToken = Cookies.get(jwtCookieName);
  }

  return jwtToken;
};

const getOneGraphOptions = (dom: Element): OneGraphServiceOptions => {
  const dpuApiGatewayUrl = getDpuApiGatewayUrl(dom);
  const oneGraphOptions = {
    oneGraphDataVersionsUrl: `${dpuApiGatewayUrl}/versions`,
    oneGraphUrl: getMetaContent(dom, 'cola-api-url'),
    ...getPreviewJwtTokenOption(dom),
  };

  return oneGraphOptions;
};

const getPreviewJwtTokenOption = (
  dom: Element,
): AudiColaServiceOptions | OneGraphServiceOptions => {
  const jwtToken = getPreviewToken(dom);
  if (jwtToken) {
    return { preview: { jwtToken: jwtToken } };
  }

  return {};
};

const getColaOptions = (dom: Element): AudiColaServiceOptions => {
  const dpuApiGatewayUrl = getDpuApiGatewayUrl(dom);
  const colaOptions = {
    colaDataVersionsUrl: `${dpuApiGatewayUrl}/versions`,
    colaUrl: getMetaContent(dom, 'cola-api-url'),
    ...getPreviewJwtTokenOption(dom),
  };

  return colaOptions;
};

const getConfiguratorLinkOptions = (
  dom: Element,
): AudiConfiguratorLinkServiceOptions => {
  const apiGateWayUrl = getApiGatewayUrl(dom);
  const localeServiceDefinition = getLocaleOptions(dom);
  const language = localeServiceDefinition.language;
  const country = localeServiceDefinition.countryCode.toLowerCase();

  return {
    apiGateWayUrl: `${apiGateWayUrl}/carlinelinks/v1/${country}/${language}`,
  };
};

const getCurrentCarlineOptions = (
  dom: Element,
): CurrentCarlineServiceConfig => {
  return {
    carline: getMetaContent(dom, 'carline'),
    carlineGroup: getMetaContent(dom, 'carlinegroup'),
    prstring: getMetaContent(dom, 'prstring'),
  };
};

const getEnvConfigOptions = (dom: Element): EnvConfigServiceOptions => {
  const sysEnv = getSysEnv(getApiGatewayUrl(dom));
  const envConfigBaseUrl = 'https://assets.audi.com/audi-env-config/0';

  // country specific cases
  const localeServiceDefinition = getLocaleOptions(dom);

  let templateUrl = `${envConfigBaseUrl}/config/${sysEnv}/{key}.json`;
  // us / AoA?
  if (localeServiceDefinition.countryCode.toLowerCase() === 'us') {
    templateUrl = `${envConfigBaseUrl}/config/aoa/${sysEnv}/{key}.json`;
  }

  return {
    templateUrl,
  };
};

const gfaServicesDefaultConfigurationGfaScope = {
  gfa: {},
};

const gfaServicesDefaultConfigurationOneshopBffPart = {
  id: '?',
  origin: window.location.origin,
  path: '/',
};

const gfaServicesDefaultConfigurationOneshopGlobalFSBaseurlPart = {
  id: '?',
  origin: window.location.origin,
  path: '/',
};

const gfaServicesDefaultConfigurationOneshopUbffPart = {
  id: '?',
  origin: window.location.origin,
  path: '/',
};

const gfaServicesDefaultConfiguration = {
  oneshopBff: { ...gfaServicesDefaultConfigurationOneshopBffPart },
  oneshopGlobalFSBaseurl: {
    ...gfaServicesDefaultConfigurationOneshopGlobalFSBaseurlPart,
  },
  oneshopUbff: { ...gfaServicesDefaultConfigurationOneshopUbffPart },
};

const getGfaServiceConfig = (): GfaServiceConfigProviderV1 => {
  const logger = createLogger();

  if (typeof window.SETUPS === 'undefined') {
    logger.info(
      '`window.SETUPS` is not defined. Some services may not retrieve data.',
    );
    return { gfa: { ...gfaServicesDefaultConfiguration } };
  }

  const gfaServiceConfig = window.SETUPS.get('gfaServiceConfig');

  if (typeof gfaServiceConfig !== 'string' || gfaServiceConfig === '') {
    return { gfa: { ...gfaServicesDefaultConfiguration } };
  }
  try {
    let parsedConfiguration = JSON.parse(gfaServiceConfig);
    if (parsedConfiguration) {
      // ensure the default properties are available to avoid crashing the service
      parsedConfiguration = {
        ...gfaServicesDefaultConfigurationGfaScope,
        ...parsedConfiguration,
      };
      parsedConfiguration.gfa = {
        ...gfaServicesDefaultConfiguration,
        ...parsedConfiguration.gfa,
      };
      parsedConfiguration.gfa.oneshopBff = {
        ...gfaServicesDefaultConfigurationOneshopBffPart,
        ...parsedConfiguration.gfa.oneshopBff,
      };
      parsedConfiguration.gfa.oneshopGlobalFSBaseurl = {
        ...gfaServicesDefaultConfigurationOneshopGlobalFSBaseurlPart,
        ...parsedConfiguration.gfa.oneshopGlobalFSBaseurl,
      };
      parsedConfiguration.gfa.oneshopUbff = {
        ...gfaServicesDefaultConfigurationOneshopUbffPart,
        ...parsedConfiguration.gfa.oneshopUbff,
      };
      return parsedConfiguration;
    }

    return { gfa: { ...gfaServicesDefaultConfiguration } };
  } catch (err) {
    logger.error(err);
    return { gfa: { ...gfaServicesDefaultConfiguration } };
  }
};

const getDisclaimerManagerOptions = (): DisclaimerManagerOptions => {
  return {
    defaultLegalEntity: 'audi',
    legalEntityLabels: { audi: 'Audi' },
  };
};

const getI18nOptions = (dom: Element): I18NServiceOptions => {
  return {
    i18nEndpointUrl: getMetaContent(dom, 'i18n-endpoint'),
  };
};

const getLocaleOptions = (dom: Element): LocaleServiceConfig => {
  return {
    brand: 'A',
    countryCode: getMetaContent(dom, 'locale-market') || 'DE',
    currency: getMetaContent(dom, 'locale-currency') || 'EUR',
    language: getMetaContent(dom, 'locale-language') || 'de',
    market: undefined,
  };
};

const getMarketContextServiceOptions = (
  dom: Element,
): AudiMarketContextServiceOptions => {
  return {
    v2: {
      apiGatewayUrl: getMarketContextApiGatewayUrl(dom),
    },
  };
};

const getNavigationOptions: () => NavigationServiceOptions = () => {
  const logger = createLogger();

  if (typeof window.SETUPS === 'undefined') {
    logger.info(
      '`window.SETUPS` is not defined. Some services may not retrieve data.',
    );
    return {};
  }

  const navigationServiceOptions = window.SETUPS.get(
    'navigationServiceOptions',
  );

  if (
    typeof navigationServiceOptions !== 'string' ||
    navigationServiceOptions === ''
  ) {
    return {};
  }
  try {
    return JSON.parse(navigationServiceOptions);
  } catch (err) {
    logger.error(err);
    return {};
  }
};

const getRenderModeOptions = (): RenderMode => {
  const renderMode = document.body.className.includes('cq-wcm-edit')
    ? RenderMode.EDIT
    : RenderMode.DEFAULT;
  return renderMode;
};

const getTrackingOptions = (): TrackingServiceOptions => {
  const logger = createLogger();

  return {
    datalayer: window.digitalData,
    sanitizeUrl: (url: string): string => removePrivateData(url, logger),
  };
};

const getVueFormatterOptions = (dom: Element): VueFormatterServiceOptions => {
  let vueAgwEndpoint = getMetaContent(dom, 'vue-agw-endpoint');

  try {
    // test if the url is valid
    const url = new URL(vueAgwEndpoint);
    if (url.host) {
      if (!url.host.endsWith('audi.com')) {
        const logger = createLogger();
        logger.error(
          `invalid url for vue agw endpoint detected: ${vueAgwEndpoint}. Will fallback to default endpoint`,
        );
        vueAgwEndpoint = null;
      }
    }
  } catch (error) {
    const logger = createLogger();
    logger.error(
      `Error parsing url: ${vueAgwEndpoint}. Will fallback to default endpoint`,
      error,
    );
    vueAgwEndpoint = null;
  }

  // fallback to default url if no valid url is provided
  if (!vueAgwEndpoint) {
    vueAgwEndpoint = 'https://vue-agw.assets.audi.com';
  }

  const vueFormatterOptions: VueFormatterServiceOptions = {
    VUE_AGW_ENDPOINT: vueAgwEndpoint,
  };

  return vueFormatterOptions;
};

const prepareWindow = function (): void {
  window.featureServiceDefinitions = window.featureServiceDefinitions || [];

  // if there is no datalayer we provide one. as convenience for the developer
  if (typeof window.digitalData === 'undefined') {
    window.digitalData = { event: [] };
  }
};

const getCookieServiceOption = function (): CookieServiceOptions {
  const logger = createLogger();
  let useFallbackCookieManagement = true;
  if (typeof window.SETUPS !== 'undefined') {
    const scopes = window.SETUPS.get('nemo.env.scopes');
    useFallbackCookieManagement = scopes && scopes.includes('COOKIE_POLICY');
  } else {
    logger.warn(
      '`window.SETUPS` is not defined. Fallback to fallback consent manager',
    );
  }

  return {
    consentManager: useFallbackCookieManagement
      ? createFallbackConsentManager()
      : createEnsightenConsentManager(),
  };
};

const getCarlineLinkServiceConfig = function (
  dom: Element,
): CarlineLinkServiceOptions {
  const carlineLinkServiceOptions: CarlineLinkServiceOptions = {};
  carlineLinkServiceOptions.carlineLinkEndpointUrl = `${getApiGatewayUrl(
    dom,
  )}/carlinelinks/v1/${getLocaleOptions(dom).countryCode.toLowerCase()}/${
    getLocaleOptions(dom).language
  }`;
  return carlineLinkServiceOptions;
};

function getStateManagerConfig(dom: Element): StateManagerServiceOptions {
  const searchParams = new URLSearchParams(window.location.search);
  const debug = searchParams.has('debug');

  // TODO: correctly set the carlineID2Urls

  return {
    carlineID2Urls: { q4etron: { baseurl: '/' } },
    carlineId: getMetaContent(dom, 'carline'),
    debug,
    enableSimpleConflictResolution: searchParams.has(
      'simpleConflictResolution',
    ),
    enableSingleQueryMode: true,
  };
}

export {
  getDefaultLogoutRedirectUrl,
  getApiGatewayUrl,
  getAuthProxyUrl,
  getAuthOptions,
  getColaOptions,
  getConfiguratorLinkOptions,
  getCookieServiceOption,
  getCurrentCarlineOptions,
  getCarlineLinkServiceConfig,
  getDisclaimerManagerOptions,
  getDpuApiGatewayUrl,
  getMarketContextApiGatewayUrl,
  getEnvConfigOptions,
  getGfaServiceConfig,
  getI18nOptions,
  getOneGraphOptions,
  getLocaleOptions,
  getMarketContextServiceOptions,
  getNavigationOptions,
  getPreviewToken as __getPreviewToken,
  getRenderModeOptions,
  getTrackingOptions,
  getVueFormatterOptions,
  prepareWindow,
  getStateManagerConfig,
  gfaServicesDefaultConfiguration,
};
